import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage-angular';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  GOOGLE_AUTH_CALENDAR_TOKEN,
  GOOGLE_AUTH_TOKEN,
  GUEST,
  IS_MODE_MENU,
  KPI_STATES,
  LAST_PAGE,
  LEGAL_ACCEPTED_VERSIONS,
  PLATFORM,
  PREFER_MODERN_LANGUAGE,
  SHARE_LAST_TEMPLATE,
  PUSH_NOTIFICATIONS,
  PUSH_NOTIFICATIONS_ACTIVE,
  PWA_INSTALL_BANNER_DISMISSED,
  SYSTEM_INFO,
  SYSTEM_MENU,
  THEME,
  TOKEN,
  TOKEN_REFRESH,
  USER,
} from '@constants/app.constants';
import { StoredNotification } from 'src/types/notifications';

@Injectable({
  providedIn: 'root',
})
export class StorageLoader {
  private modeMenu$ = new BehaviorSubject<boolean>(true);

  constructor(private storage: Storage) {}

  private readyPromise?: Promise<void>;

  async create(): Promise<void> {
    if (this.readyPromise) return this.readyPromise;
    this.readyPromise = (async () => {
      await this.storage.create();
    })();
    return this.readyPromise;
  }

  async ready(): Promise<void> {
    return this.create();
  }

  async clear() {
    await this.storage.clear();
  }

  async getUser() {
    return await this.storage.get(USER);
  }

  async setUser(user: any) {
    return await this.storage.set(USER, user);
  }

  async getToken() {
    return await this.storage.get(TOKEN);
  }

  async setToken(token: string) {
    return await this.storage.set(TOKEN, token);
  }

  async getRefreshToken() {
    return await this.storage.get(TOKEN_REFRESH);
  }

  async setRefreshToken(token: string) {
    return await this.storage.set(TOKEN_REFRESH, token);
  }

  async getLastPage() {
    return await this.storage.get(LAST_PAGE);
  }

  async setLastPage(lastPage: string) {
    return await this.storage.set(LAST_PAGE, lastPage);
  }

  async getSystemInfo() {
    return await this.storage.get(SYSTEM_INFO);
  }

  async setSystemInfo(systemInfo: any) {
    return await this.storage.set(SYSTEM_INFO, systemInfo);
  }

  async getPlatform() {
    return await this.storage.get(PLATFORM);
  }

  async setPlatform(platform: string) {
    return await this.storage.set(PLATFORM, platform);
  }

  async getSystemMenu() {
    return await this.storage.get(SYSTEM_MENU);
  }

  async setSystemMenu(systemMenu: any) {
    return await this.storage.set(SYSTEM_MENU, systemMenu);
  }

  async getIsModeMenu() {
    let mode = await this.storage.get(IS_MODE_MENU);

    if (mode === null || mode === undefined) {
      const platform = await this.getPlatform();

      if (platform === 'desktop') {
        mode = true;
      } else if (platform === 'mobile') {
        mode = false;
      }

      if (environment.ENVIRONMENT === 'client') {
        mode = false;
      } else if (environment.ENVIRONMENT === 'system') {
        mode = true;
      }
    }

    this.modeMenu$.next(mode);
    return mode;
  }

  async getIsModeMenuObs() {
    const mode = await this.getIsModeMenu();
    this.modeMenu$.next(mode);

    return this.modeMenu$.asObservable();
  }

  async changeModeMenu() {
    const isModeMenu = await this.getIsModeMenu();

    if (
      isModeMenu === null ||
      isModeMenu === undefined ||
      isModeMenu === false
    ) {
      this.modeMenu$.next(true);
      return await this.storage.set(IS_MODE_MENU, true);
    } else {
      this.modeMenu$.next(false);
      return await this.storage.set(IS_MODE_MENU, false);
    }
  }

  async setGoogleAuth(googleAuth: any) {
    return await this.storage.set(GOOGLE_AUTH_TOKEN, googleAuth);
  }

  async getGoogleAuth() {
    return await this.storage.get(GOOGLE_AUTH_TOKEN);
  }

  async setGoogleAuthCalendar(googleAuth: any) {
    return await this.storage.set(GOOGLE_AUTH_CALENDAR_TOKEN, googleAuth);
  }

  async getGoogleAuthCalendar() {
    return await this.storage.get(GOOGLE_AUTH_CALENDAR_TOKEN);
  }

  async getPwaBannerDismissed() {
    return await this.storage.get(PWA_INSTALL_BANNER_DISMISSED);
  }

  async setPwaBannerDismissed() {
    return await this.storage.set(PWA_INSTALL_BANNER_DISMISSED, true);
  }

  async getGuest() {
    return await this.storage.get(GUEST);
  }

  async setGuest(guest: any) {
    return await this.storage.set(GUEST, guest);
  }

  async getTheme() {
    return await this.storage.get(THEME);
  }

  async setTheme(theme: string) {
    return await this.storage.set(THEME, theme);
  }

  async getPreferModernLanguage(): Promise<boolean> {
    const value = await this.storage.get(PREFER_MODERN_LANGUAGE);
    return value === true;
  }

  async setPreferModernLanguage(value: boolean): Promise<void> {
    await this.storage.set(PREFER_MODERN_LANGUAGE, !!value);
  }

  async getLastShareTemplate(): Promise<string | null> {
    return await this.storage.get(SHARE_LAST_TEMPLATE);
  }

  async setLastShareTemplate(templateId: string): Promise<void> {
    await this.storage.set(SHARE_LAST_TEMPLATE, templateId);
  }

  async saveNotification(notification: StoredNotification): Promise<void> {
    const list = await this.getNotifications();

    if (this.findNotificationDuplicate(list, notification)) {
      return;
    }

    list.unshift(notification);

    await this.storage.set(PUSH_NOTIFICATIONS, list);
  }

  async getNotifications(): Promise<StoredNotification[]> {
    const list: StoredNotification[] =
      (await this.storage.get(PUSH_NOTIFICATIONS)) || [];

    const deduped = this.dedupeNotifications(list);

    if (deduped.length !== list.length) {
      await this.storage.set(PUSH_NOTIFICATIONS, deduped);
    }

    return deduped;
  }

  private dedupeNotifications(
    list: StoredNotification[],
  ): StoredNotification[] {
    const seenIds = new Set<string>();
    const seenContent = new Set<string>();
    const result: StoredNotification[] = [];

    for (const item of list) {
      if (item?.id && seenIds.has(item.id)) continue;

      const contentKey = this.notificationContentKey(item);
      if (contentKey && seenContent.has(contentKey)) continue;

      if (item?.id) seenIds.add(item.id);
      if (contentKey) seenContent.add(contentKey);
      result.push(item);
    }

    return result;
  }

  private findNotificationDuplicate(
    list: StoredNotification[],
    candidate: StoredNotification,
  ): StoredNotification | null {
    if (candidate?.id) {
      const byId = list.find((item) => item?.id === candidate.id);
      if (byId) return byId;
    }

    const candidateKey = this.notificationContentKey(candidate);
    if (!candidateKey) return null;

    const byContent = list.find(
      (item) => this.notificationContentKey(item) === candidateKey,
    );
    return byContent ?? null;
  }

  private notificationContentKey(
    notification: StoredNotification,
  ): string | null {
    if (!notification) return null;

    const title = (notification.title || '').trim();
    const body = (notification.body || '').trim();

    if (!title && !body) return null;

    const ts = notification.receivedAt
      ? new Date(notification.receivedAt).getTime()
      : NaN;
    const bucket = Number.isFinite(ts)
      ? Math.floor(ts / (5 * 60 * 1000))
      : 'no-ts';

    return `${title}|${body}|${bucket}`;
  }

  async markAsReadNotification(id: string): Promise<void> {
    const list = await this.getNotifications();
    const updated = list.map((n: any) =>
      n.id === id ? { ...n, read: true } : n,
    );

    await this.storage.set(PUSH_NOTIFICATIONS, updated);
  }

  async markAsReadAllNotifications(): Promise<void> {
    const list = await this.getNotifications();
    const updated = list.map((n: any) => ({ ...n, read: true }));

    await this.storage.set(PUSH_NOTIFICATIONS, updated);
  }

  async getUnreadNotificationsCount(): Promise<number> {
    const list = await this.getNotifications();

    return list.filter((n: any) => !n.read).length;
  }

  async clearNotifications(): Promise<void> {
    await this.storage.set(PUSH_NOTIFICATIONS, []);
  }

  async setNotificationPushActive(active: boolean) {
    return await this.storage.set(PUSH_NOTIFICATIONS_ACTIVE, active);
  }

  async getNotificationPushActive() {
    return await this.storage.get(PUSH_NOTIFICATIONS_ACTIVE);
  }

  async getKpiState(key: string): Promise<boolean | null> {
    const states = (await this.storage.get(KPI_STATES)) || {};
    return typeof states[key] === 'boolean' ? states[key] : null;
  }

  async setKpiState(key: string, isOpen: boolean): Promise<void> {
    const states = (await this.storage.get(KPI_STATES)) || {};
    states[key] = isOpen;
    await this.storage.set(KPI_STATES, states);
  }

  async getLegalAcceptedVersion(kind: string): Promise<string | null> {
    const map = (await this.storage.get(LEGAL_ACCEPTED_VERSIONS)) || {};
    return typeof map[kind] === 'string' ? map[kind] : null;
  }

  async setLegalAcceptedVersion(kind: string, version: string): Promise<void> {
    const map = (await this.storage.get(LEGAL_ACCEPTED_VERSIONS)) || {};
    map[kind] = version;
    await this.storage.set(LEGAL_ACCEPTED_VERSIONS, map);
  }
}
